import React, { useContext, useMemo, useRef, useState } from "react"
import { Animated, StyleSheet, Text, TouchableOpacity, View, Pressable, Platform } from "react-native"
import { ThemeContext } from "./utility/context/ThemeContext"
import { currencyFormat, mapPlaidAccountToNetWorthEntry } from "@ecountabl/lib"
import { MAX_CONTENT_WIDTH } from "./config"
import { ShadowView, textStyles } from "./style"
import { useAppSelector } from "./store"
import Bullet from "./style/bullet"
import { Ionicons } from "@expo/vector-icons"
import { CustomActionSheet, CustomActionSheetRef } from "./utility/CustomActionSheet"
import { usePopover, showPopoverFromRef } from "./utility/popover"
import SpecifyValues from "./specify-values"
// import ReferralForm from "./referral-form"
import { usePlaidLink } from "./utility/context"
import Haptics from "./utility/haptics"
import AddCompanyForm from "./add-company-form"
import Analytics from "./utility/analytics"
import { SkeletonGroup, SkeletonLine } from "./utility/skeleton"

type QuickAction = {
  title: string
  icon: React.ReactNode
  color: string
  onPress: () => void
}

const TotalBalance:React.FC = () => {
  const {
    status: accountsStatus,
    getAccountsError: accountsError,
    accounts,
    institutions
  } = useAppSelector(state => state.accountsSlice)
  const theme = useContext(ThemeContext)
  const {
    bgMain, borderColor, danger, white, primary, darkAccent, lightAccent, textMain
  } = theme.theme.colors
  const specifyValuesSheetRef = useRef<CustomActionSheetRef>(null)
  // const referBusinessSheetRef = useRef<CustomActionSheetRef>(null)
  const addBrandSheetRef = useRef<CustomActionSheetRef>(null)
  const animatedHeight = useRef(new Animated.Value(0)).current
  const { showFromAnchor } = usePopover()
  const infoAnchorRef = useRef<View>(null)
  const openTimerRef = useRef<number | null>(null)

  const { startPlaidLinkSession } = usePlaidLink()

  const multipleInstitutions = institutions.length > 1
  const [showBreakdown, setShowBreakdown] = useState(false)
  const breakdownTargetHeight = useMemo(() => {
    // approximate row height (24) + padding (4)
    return (institutions.length * 28) + 8
  }, [institutions.length])

  const userAccountsTotal = useMemo(() => {
    const netWorthEntries = accounts.map(mapPlaidAccountToNetWorthEntry)
    // sum of values for accounts where addToTotal is true
    return netWorthEntries.reduce((acc, entry) => (
      acc + (entry.addToTotal ? entry.value : 0)
    ), 0)
  }, [accounts])

  const quickActions:QuickAction[] = useMemo(
    () => {
      const actions = [
        {
          title: "Specify Your Values",
          icon: <Ionicons name="checkmark" size={24} color={white} />,
          color: primary,
          onPress: () => {
            Analytics.track('specify_your_values_pressed')
            specifyValuesSheetRef.current?.show()
          }
        },
        {
          title: "Link External Accounts",
          icon: <Ionicons name="card-outline" size={24} color={white} />,
          color: darkAccent,
          onPress: () => {
            Analytics.track('link_external_accounts_pressed')
            startPlaidLinkSession({
              redirectPath: "dashboard",
            })
          }
        },
        {
          title: "Introduce a Business",
          icon: <Ionicons name="business-outline" size={24} color={white} />,
          color: lightAccent,
          onPress: () => {
            Analytics.track('introduce_a_business_pressed')
            addBrandSheetRef.current?.show()
          }
        }
      ]

      return actions
    }, [primary, darkAccent, lightAccent, startPlaidLinkSession, white]
  )

  const hideSheet = (ref: React.MutableRefObject<CustomActionSheetRef | null>) => {
    ref.current?.hide()
  }

  const renderBalanceMessage = () => {
    if (accountsStatus === 'idle' || accountsStatus === 'loading') {
      return (
        <SkeletonGroup>
          <SkeletonLine
            width="100%"
            height={28}
            style={{marginVertical: 4}}
          />
          <SkeletonLine
            width="70%"
            height={14}
            style={{marginVertical: 8}}
          />
        </SkeletonGroup>
      )
    }

    if (accountsStatus === 'error' || accountsError) {
      return <View style={{flexDirection: "row", marginTop: 10}}>
        <Bullet color={danger} size={6} style={{marginRight: 8}}/>
        <Text style={[textStyles.body, {color: textMain}]}>
          Error fetching accounts. Please try again later.
        </Text>
      </View>
    }

    if (accounts.length === 0) {
      return (
        <Text style={[textStyles.body, {color: textMain}]}> 
          No accounts to calculate the balance with. If you wish to add some accounts,
          you can do so by pressing the{' '}
          <Text style={{fontWeight: "bold"}}>Link External Accounts</Text> button below.
        </Text>
      )
    }

    // if balance is 0 and all institutions have errors, show a message
    if (
      userAccountsTotal === 0 &&
      institutions.length > 0 &&
      institutions.filter(acc => acc.error).length === institutions.length
    ) {
      return (
        <Text style={[textStyles.body, {color: textMain}]}>
          Unable to calculate a balance. Please relink your accounts for an accurate balance.
        </Text>
      )
    }

    return (
      <Text style={[textStyles.h1, {color: textMain}]}>
        {currencyFormat(userAccountsTotal)}
      </Text>
    )
  }

  const toggleBreakdown = () => {
    const to = showBreakdown ? 0 : 1
    setShowBreakdown(!showBreakdown)
    Haptics.selection()
    Animated.timing(animatedHeight, {
      toValue: to,
      duration: 250,
      useNativeDriver: false
    }).start()
  }

  const showInfoPopover = () => {
    showPopoverFromRef(
      infoAnchorRef,
      showFromAnchor,
      <Text style={[textStyles.label, { color: textMain, maxWidth: 260 }]}>
        This is the total of all assets linked to your account.{' '}
        Does not include credit cards, debts, etc.
      </Text>,
      undefined,
      {
        offset: { x: 0, y: 0 },
        align: 'auto',
        vertical: 'auto',
        showArrow: true,
      }
    )
  }

  const breakdownHeight = animatedHeight.interpolate({
    inputRange: [0, 1],
    outputRange: [0, breakdownTargetHeight]
  })
  return <>
    <View style={styles.container}>
      <View style={{position: "absolute", top: 0, left: 0, right: 0, backgroundColor: primary, height: 140, width: "100%"}}/>
      <View style={[styles.fg, {maxWidth: MAX_CONTENT_WIDTH}]}>
        <ShadowView
          style={[styles.centerComponent, {backgroundColor: bgMain}]}
        >
          <View style={styles.balance}>
            <View style={{flexDirection: "row", alignItems: "center", gap: 4}}>
              <Text 
                style={[textStyles.body, {color: textMain}]}
              >
                Total Deposit Balance
              </Text>
              <View style={{flex: 1, justifyContent: "center", alignItems: "center"}}>
                  <Pressable
                    ref={infoAnchorRef}
                    onPress={Platform.OS !== 'web' ? () => {
                      showInfoPopover()
                    } : undefined}
                    onHoverIn={Platform.OS === 'web' ? () => {
                      if (openTimerRef.current) {
                        clearTimeout(openTimerRef.current)
                      }
                      openTimerRef.current = setTimeout(() => {
                        showInfoPopover()
                      }, 150) as unknown as number
                    } : undefined}
                    hitSlop={10}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    <Ionicons name="information-circle-outline" size={16} color={textMain}/>
                  </Pressable>
              </View>
              
            </View>
            
            { renderBalanceMessage() }
            {
              multipleInstitutions ? (
                <View style={{width: "100%", marginTop: 12}}>
                  <TouchableOpacity
                    onPress={toggleBreakdown}
                    style={{flexDirection: "row", alignItems: "center"}}
                    hitSlop={10}
                  >
                    <Ionicons 
                      name={showBreakdown ? "chevron-up" : "chevron-down"} 
                      size={18} 
                      color={textMain} 
                      style={{marginRight: 6}}
                    />
                    <Text style={[textStyles.label, {color: textMain}]}> 
                      {showBreakdown ? "Hide" : "Show"} breakdown by institution
                    </Text>
                  </TouchableOpacity>
                  <Animated.View 
                    style={{marginTop: 8, overflow: "hidden", maxHeight: breakdownHeight}}
                  >
                    {
                      institutions.map(({name, totalBalance, error, item_id}) => (
                        <View
                          key={item_id}
                          style={{
                            flexDirection: "row", 
                            justifyContent: "space-between", 
                            width: "100%", 
                            paddingVertical: 2,
                            gap: 4
                          }}
                        >
                          <Text
                            style={[textStyles.body, {color: error ? danger : textMain, fontWeight: "bold", flex: 1}]}
                            numberOfLines={1}
                            ellipsizeMode="tail"
                          >
                            {name}
                          </Text>
                          {error && item_id ? (
                            <TouchableOpacity
                              onPress={() => {
                                startPlaidLinkSession({
                                  item_id: item_id,
                                  redirectPath: "dashboard",
                                })
                              }}
                            >
                              <Text
                                style={[textStyles.body, {color: danger}]}
                              >
                                Relink
                              </Text>
                            </TouchableOpacity>
                          ) : (
                            <Text 
                              style={[
                                textStyles.body,
                                {color: textMain}
                              ]}
                            >
                              {currencyFormat(totalBalance)}
                            </Text>
                          )}
                        </View>
                      ))
                    }
                  </Animated.View>
                </View>
              ) : null
            }
          </View>
          <View style={[styles.quickActions, {borderTopColor: borderColor}]}>
            {
              quickActions.map((action, index) => (
                <View key={index} style={styles.quickActionItem}>
                  <TouchableOpacity
                    style={styles.quickActionItemPressable}
                    onPress={() => {
                      Haptics.impactMedium()
                      action.onPress()
                    }}
                  >
                    <View style={[styles.iconButton, {backgroundColor: action.color}]}>
                      {action.icon}
                    </View>
                    <Text 
                      style={[textStyles.label, styles.quickActionText, {color: textMain}]}
                    > 
                      {action.title}
                    </Text>
                  </TouchableOpacity>
                </View>
              ))
            }
          </View>
        </ShadowView>
      </View>
    </View>
    {/* Specify Values Actionsheet */}
    <CustomActionSheet
      ref={specifyValuesSheetRef}
      name="specify-values"
    >
      <SpecifyValues
        hideSheet={() => hideSheet(specifyValuesSheetRef)}
      />
    </CustomActionSheet>
    {/* Add Business Actionsheet */}
    <CustomActionSheet
      ref={addBrandSheetRef}
      name="add-company"
    >
      <AddCompanyForm
        closeSheet={() => hideSheet(addBrandSheetRef)}
      />
    </CustomActionSheet>
  </>
}

export default TotalBalance

const styles = StyleSheet.create({
  container: {
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  fg: {
    position: "relative",
    paddingTop: 8,
    paddingHorizontal: 16,
    zIndex: 1,
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
  centerComponent: {
    position: "relative",
    width: "100%",
    // height: 260,
    paddingVertical: 20,
    paddingHorizontal: 24,
    borderRadius: 8,
  },
  balance: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    marginBottom: 10,
    width: "100%",
  },
  quickActions: {
    display: "flex",
    borderTopWidth: 1,
    paddingTop: 20,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10
  },
  quickActionItem: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  quickActionItemPressable: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: 15,
  },
  iconButton: {
    width: 48,
    height: 48,
    borderRadius: 10,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  quickActionText: {
    textAlign: "center",
    fontWeight: "500",
  }
})
